import { useEffect, useMemo, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type {
  SurveyInstrumentDto, SurveyQuestionDto, SurveySkipLogicVocabularyDto,
} from '@/shared/api/types'
import { addSkipRule } from './api'
import { PublishedOffer, RetargetedNote, publishedRefusal, type VersionOffer } from './QuestionDialog'

/**
 * One branch.
 *
 * ## The two operators that take no value, and why the input disappears
 *
 * `ANSWERED` and `NOT_ANSWERED` ask whether there is an answer at all; there is
 * nothing to compare to. Sending a `valueJson` alongside one is not an error —
 * the server ignores it — which is precisely the problem: the author sees a
 * value on the rule, believes the branch depends on it, and edits it later
 * expecting the behaviour to change. So the field is not disabled, it is gone,
 * and a sentence says why.
 *
 * ## Why the value is a picker and not a text box on a choice question
 *
 * The branch compares on the option CODE (see `skipRules.ts`), so a typo is a
 * rule that silently never fires — the worst failure available here, because a
 * branch that never fires looks exactly like a branch nobody triggered. Where
 * the source question has options, the codes come from it.
 *
 * ## What the client refuses to decide
 *
 * Nothing. Operators and actions come from `getSkipLogicVocabulary()`, targets
 * are offered even where they point backwards, and the server's refusal
 * (`survey/skip-rule-backwards`) is rendered as its own sentence — it names both
 * positions, which no client-side guard would.
 */

/**
 * Meanings, not a code list.
 *
 * The lists are the server's; these sentences describe the ones this build
 * recognises and say nothing about the ones it does not. An operator added
 * server-side still appears in the select, as its raw code, with a free-text
 * value box — forward-compatible rather than silently absent.
 */
const OPERATOR_MEANING: Record<string, string> = {
  EQ: 'The answer is exactly this option.',
  NEQ: 'The answer is anything but this option.',
  IN: 'The answer is one of these options.',
  NOT_IN: 'The answer is none of these options.',
  GT: 'The answer is greater than this number. Numeric and scale questions only.',
  LT: 'The answer is less than this number. Numeric and scale questions only.',
  ANSWERED: 'There is an answer, whatever it is.',
  NOT_ANSWERED: 'The question was left unanswered.',
}

const ACTION_MEANING: Record<string, string> = {
  SKIP_TO: 'Jump forward to the target. Everything strictly between is recorded as '
    + 'skipped-by-rule, not as blank.',
  HIDE: 'Hide the target while the condition holds.',
  SHOW: 'The target stays hidden unless the condition holds — "only ask this if", without '
    + 'having to write the negation as a second rule.',
  END_INTERVIEW: 'Stop here. Everything after the source question is skipped-by-rule and the '
    + 'interview is recorded as ended by this branch.',
}

/** The two that compare against nothing. Stated in the operator list, not inferred from it. */
const NO_VALUE = ['ANSWERED', 'NOT_ANSWERED']
/** The two whose value is a JSON array rather than a scalar. */
const LIST_OPERATORS = ['IN', 'NOT_IN']
/** The two the preview cannot evaluate — `skipRules.ts` only ever holds an option code. */
const NUMERIC_OPERATORS = ['GT', 'LT']

type TargetKind = 'QUESTION' | 'BLOCK' | 'NONE'

export function SkipRuleDialog({
  open, versionId, instrument, vocabulary, initialSourceQuestionId, offer, onClose, onSaved,
}: {
  open: boolean
  versionId: string
  /** The document as it stands. Sources, targets and option codes all come from it. */
  instrument: SurveyInstrumentDto
  /** `getSkipLogicVocabulary()`, or null while it loads. */
  vocabulary: SurveySkipLogicVocabularyDto | null
  /** Prefilled when the author started from a question in the outline. */
  initialSourceQuestionId: string | null
  offer: VersionOffer
  onClose: () => void
  onSaved: (next: SurveyInstrumentDto) => void
}) {
  const [sourceQuestionId, setSourceQuestionId] = useState('')
  const [operatorCode, setOperatorCode] = useState('')
  const [actionCode, setActionCode] = useState('')
  const [singleValue, setSingleValue] = useState('')
  const [listValue, setListValue] = useState<string[]>([])
  const [rawValue, setRawValue] = useState('')
  const [targetKind, setTargetKind] = useState<TargetKind>('QUESTION')
  const [targetQuestionId, setTargetQuestionId] = useState('')
  const [targetBlockId, setTargetBlockId] = useState('')
  const [position, setPosition] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<{ sentence: string; versionId: string } | null>(null)

  const ordered = useMemo(
    () => [...instrument.questions].sort((a, b) => a.position - b.position),
    [instrument.questions])

  useEffect(() => {
    if (!open) return
    setSourceQuestionId(initialSourceQuestionId ?? '')
    setOperatorCode('')
    setActionCode('')
    setSingleValue('')
    setListValue([])
    setRawValue('')
    setTargetKind('QUESTION')
    setTargetQuestionId('')
    setTargetBlockId('')
    setPosition(String(instrument.skipRules.length + 1))
    setError(null)
    setRefusal(null)
  }, [open, initialSourceQuestionId, instrument.skipRules.length])

  const source = ordered.find((q) => q.id === sourceQuestionId) ?? null
  const sourceOptions = source?.options ?? []
  const takesValue = operatorCode !== '' && !NO_VALUE.includes(operatorCode)
  const isList = LIST_OPERATORS.includes(operatorCode)
  const isNumeric = NUMERIC_OPERATORS.includes(operatorCode)
  const endsInterview = actionCode === 'END_INTERVIEW'
  const movedOn = refusal !== null && refusal.versionId !== versionId

  /** Codes, never labels: relabelling an option must not change which branch fires. */
  const useOptionPicker = takesValue && !isNumeric && sourceOptions.length > 0

  const valueJson = useMemo(() => {
    if (!takesValue) return null
    if (isList) return listValue.length === 0 ? '' : JSON.stringify(listValue)
    if (useOptionPicker) return singleValue === '' ? '' : JSON.stringify(singleValue)
    if (isNumeric) return rawValue.trim() === '' ? '' : JSON.stringify(Number(rawValue))
    return rawValue.trim()
  }, [takesValue, isList, listValue, useOptionPicker, singleValue, isNumeric, rawValue])

  /** An unparseable value does not fire — in the preview and in the server's evaluator. */
  const valueParses = useMemo(() => {
    if (valueJson === null || valueJson === '') return true
    try { JSON.parse(valueJson); return true } catch { return false }
  }, [valueJson])

  const target = ordered.find((q) => q.id === targetQuestionId) ?? null
  const pointsBackwards = !!source && !!target && target.position <= source.position

  async function submit() {
    setBusy(true)
    setError(null)
    setRefusal(null)
    try {
      const next = await addSkipRule(versionId, {
        sourceQuestionId: sourceQuestionId || null,
        operatorCode,
        // Omitted rather than sent empty for the two operators that ignore it —
        // a stored value nothing reads is a lie the next author will act on.
        valueJson: takesValue ? valueJson : null,
        actionCode,
        targetQuestionId: targetKind === 'QUESTION' && !endsInterview
          ? (targetQuestionId || null) : null,
        targetBlockId: targetKind === 'BLOCK' && !endsInterview ? (targetBlockId || null) : null,
        position: position.trim() === '' ? null : Number(position),
      })
      onSaved(next)
    } catch (e) {
      // `survey/skip-rule-backwards` arrives here naming both positions. So does
      // the missing-target 400 and the unknown-operator 400 — all rendered as the
      // server wrote them, because the server is the one that checked.
      const frozen = publishedRefusal(e)
      if (frozen) setRefusal({ sentence: frozen, versionId })
      else setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title="New branch"
      description="Evaluated in position order, against the answer to one question. A branch may only act on something not asked yet."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : 'Add branch'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        {refusal && !movedOn && <PublishedOffer sentence={refusal.sentence} offer={offer} />}
        {movedOn && <RetargetedNote versionNo={offer.nextVersionNo} keyedToARow />}

        <Field label="When the answer to" required
          hint="The question this branch reads. Only questions in this version can be a source.">
          <select className="erp-input h-9 w-full" value={sourceQuestionId}
            onChange={(e) => {
              setSourceQuestionId(e.target.value)
              // The old codes belong to the old question's card.
              setSingleValue('')
              setListValue([])
            }}>
            <option value="">Choose a question…</option>
            {ordered.map((q) => (
              <option key={q.id} value={q.id}>{q.position}. {q.code} · {q.text}</option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-5">
            <Field label="Operator" required
              hint={OPERATOR_MEANING[operatorCode]
                ?? (vocabulary ? 'From the server\'s own list.' : 'Loading the operators…')}>
              <select className="erp-input h-9 w-full font-mono text-erp-sm" value={operatorCode}
                onChange={(e) => setOperatorCode(e.target.value)}>
                <option value="">Choose…</option>
                {(vocabulary?.operator ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-span-7">
            {takesValue ? (
              <ValueInput
                isList={isList}
                isNumeric={isNumeric}
                useOptionPicker={useOptionPicker}
                source={source}
                singleValue={singleValue}
                setSingleValue={setSingleValue}
                listValue={listValue}
                setListValue={setListValue}
                rawValue={rawValue}
                setRawValue={setRawValue}
              />
            ) : (
              <div className="pt-6">
                <p className="text-erp-xs text-muted-foreground">
                  {operatorCode === ''
                    ? 'Pick an operator and the value it compares against appears here.'
                    : `${operatorCode} asks whether there is an answer at all, so there is `
                      + 'nothing to compare against and no value is sent. A value stored beside '
                      + 'it would be read by nobody and edited by somebody.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {takesValue && !valueParses && (
          <Note tone="bad">
            This is not valid JSON, and an unparseable value does not fire — not in the preview
            and not in the server&apos;s evaluator. A branch that never fires is
            indistinguishable from one nobody triggered, which is why this is worth stopping
            here. A string is <span className="font-mono">&quot;YES&quot;</span> with the quotes;
            a list is <span className="font-mono">[&quot;A&quot;,&quot;B&quot;]</span>.
          </Note>
        )}

        {isNumeric && (
          <Note tone="info">
            The builder preview only offers option answers, so a {operatorCode} rule shows as
            never firing there. That is the preview being honest about what it can evaluate, not
            the rule being broken — the server compares it against the real number at capture.
          </Note>
        )}

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-5">
            <Field label="Then" required
              hint={ACTION_MEANING[actionCode]
                ?? (vocabulary ? 'From the server\'s own list.' : 'Loading the actions…')}>
              <select className="erp-input h-9 w-full font-mono text-erp-sm" value={actionCode}
                onChange={(e) => {
                  setActionCode(e.target.value)
                  if (e.target.value === 'END_INTERVIEW') setTargetKind('NONE')
                  else if (targetKind === 'NONE') setTargetKind('QUESTION')
                }}>
                <option value="">Choose…</option>
                {(vocabulary?.action ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-span-4">
            <Field label="Acting on"
              hint="A block target acts on every question in it, so adding a seventh question to a section does not leave the rule covering six.">
              <select className="erp-input h-9 w-full" value={targetKind}
                disabled={endsInterview}
                onChange={(e) => setTargetKind(e.target.value as TargetKind)}>
                <option value="QUESTION">A question</option>
                <option value="BLOCK">A block</option>
                <option value="NONE">Nothing</option>
              </select>
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="Position"
              hint="Rules are evaluated in this order; a later rule can undo an earlier one.">
              <input className="erp-input h-9 w-full text-right tabular-nums" type="number" min={1}
                value={position} onChange={(e) => setPosition(e.target.value)} />
            </Field>
          </div>
        </div>

        {endsInterview && (
          // Rule 3: the reason for a disabled control goes in visible text beside
          // it, because a `title` on a disabled <select> is unreachable.
          <p className="text-erp-xs text-muted-foreground -mt-1">
            END_INTERVIEW acts on the interview itself, so there is nothing to point it at. The
            target picker is fixed at Nothing for that reason.
          </p>
        )}

        {targetKind === 'QUESTION' && !endsInterview && (
          <Field label="Target question" required
            hint="Every question is offered. One asked at or before the source is refused by the server, which names both positions.">
            <select className="erp-input h-9 w-full" value={targetQuestionId}
              onChange={(e) => setTargetQuestionId(e.target.value)}>
              <option value="">Choose a question…</option>
              {ordered.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.position}. {q.code} · {q.text}
                  {source && q.position <= source.position ? '  — asked at or before the source' : ''}
                </option>
              ))}
            </select>
          </Field>
        )}

        {targetKind === 'BLOCK' && !endsInterview && (
          <Field label="Target block" required
            hint={instrument.blocks.length === 0
              ? 'This version has no blocks yet. Add one from the outline first.'
              : 'The rule acts on every question in the block.'}>
            <select className="erp-input h-9 w-full" value={targetBlockId}
              onChange={(e) => setTargetBlockId(e.target.value)}>
              <option value="">Choose a block…</option>
              {instrument.blocks.map((b) => (
                <option key={b.id} value={b.id}>{b.code}{b.title ? ` · ${b.title}` : ''}</option>
              ))}
            </select>
          </Field>
        )}

        {pointsBackwards && (
          <Note tone="bad">
            {source?.code} is asked at position {source?.position} and {target?.code} at{' '}
            {target?.position}. A branch reading an answer that does not exist yet cannot fire —
            by the time the source is answered the target has already been read out. The server
            refuses this and says the same thing; the rule is offered anyway so the refusal
            arrives with its reasoning rather than as a control that does nothing.
          </Note>
        )}

        {source && operatorCode && actionCode && (
          <Note tone="info">
            <span className="font-medium">In words:</span> if{' '}
            <span className="font-mono">{source.code}</span> {operatorCode}
            {takesValue && valueJson ? ' ' : ''}
            <span className="font-mono">{takesValue ? valueJson : ''}</span>
            {' → '}{ACTION_MEANING[actionCode]?.toLowerCase() ?? actionCode}
            {targetKind === 'QUESTION' && target ? ` Target: ${target.code}.` : ''}
            {targetKind === 'BLOCK' && targetBlockId
              ? ` Target: block ${instrument.blocks.find((b) => b.id === targetBlockId)?.code}.`
              : ''}
            {' '}Tick the answer in the preview after saving and the outline dims what this hides —
            that is the same evaluator, run against the same rules.
          </Note>
        )}
      </div>
    </Modal>
  )
}

/**
 * The value half of the condition.
 *
 * Split out because it is four inputs wearing one label, and inlining the branch
 * in the form body made it impossible to see that the option picker is the
 * default and free text the fallback rather than the other way round.
 */
function ValueInput({
  isList, isNumeric, useOptionPicker, source,
  singleValue, setSingleValue, listValue, setListValue, rawValue, setRawValue,
}: {
  isList: boolean
  isNumeric: boolean
  useOptionPicker: boolean
  source: SurveyQuestionDto | null
  singleValue: string
  setSingleValue: (v: string) => void
  listValue: string[]
  setListValue: (v: string[]) => void
  rawValue: string
  setRawValue: (v: string) => void
}) {
  const options = source?.options ?? []

  if (useOptionPicker && isList) {
    return (
      <div className="space-y-1.5">
        <span className="text-erp-xs font-medium text-muted-foreground block">
          Any of these<span className="text-red-600 ml-0.5">*</span>
        </span>
        <div className="rounded border border-border p-2 max-h-32 overflow-y-auto space-y-1">
          {options.map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-erp-sm">
              <input type="checkbox" checked={listValue.includes(o.code)}
                onChange={(e) => setListValue(e.target.checked
                  ? [...listValue, o.code]
                  : listValue.filter((c) => c !== o.code))} />
              <span className="font-mono text-erp-xs text-muted-foreground">{o.code}</span>
              <span className="min-w-0 truncate">{o.label}</span>
            </label>
          ))}
        </div>
        <p className="text-erp-xs text-muted-foreground">
          Sent as a JSON array of option codes, which is what the evaluator compares against.
        </p>
      </div>
    )
  }

  if (useOptionPicker) {
    return (
      <Field label="This option" required
        hint="The option CODE, from this question's own card — not its label, which is editable.">
        <select className="erp-input h-9 w-full" value={singleValue}
          onChange={(e) => setSingleValue(e.target.value)}>
          <option value="">Choose an option…</option>
          {options.map((o) => (
            <option key={o.id} value={o.code}>{o.code} · {o.label}</option>
          ))}
        </select>
      </Field>
    )
  }

  if (isNumeric) {
    return (
      <Field label="This number" required
        hint="Compared against the answer's numeric value at capture.">
        <input className="erp-input h-9 w-full text-right tabular-nums" type="number" step="any"
          value={rawValue} onChange={(e) => setRawValue(e.target.value)} placeholder="35" />
      </Field>
    )
  }

  return (
    <Field label="Value (JSON)" required
      hint={source && source.options.length === 0
        ? `${source.code} has no options to pick from, so the value is typed as JSON: a string in quotes, a list in brackets.`
        : 'Typed as JSON: a string in quotes, a list in brackets.'}>
      <input className="erp-input h-9 w-full font-mono text-erp-sm" value={rawValue}
        maxLength={4000} placeholder='"YES"'
        onChange={(e) => setRawValue(e.target.value)} />
    </Field>
  )
}
